import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, GraduationCap } from 'lucide-react';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0047AB] flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Exam Trainer</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 md:p-12 shadow-sm">
          <h1 className="text-3xl font-bold mb-2">Terms of Use</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">Last updated: 10 February 2026</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-600 dark:text-slate-300 mb-8">
              These Terms of Use govern access to and use of the website and digital exam preparation platform operated by Margolytics Ltd., 128 City Road, London, EC1V 2NX, United Kingdom ("Provider", "we", "us", "our").
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              By accessing or using the Platform, you agree to be bound by these Terms. Continued use after updates constitutes acceptance of the revised Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Scope of Services</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              The Platform provides digital exam preparation services including mock exams, practice questions, quizzes, progress tracking, analytics, translations, and related educational content ("Content"). All services are delivered exclusively in digital form.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Educational Purpose & Independent Provider</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              The Platform is an independent exam preparation service. Content is provided solely for educational and training purposes and does not constitute official exam material, certification, or professional advice.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Use of AI-Supported and Third-Party Technologies</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              The Platform uses AI-supported and automated technologies, including third-party systems. AI-assisted outputs may be inaccurate, incomplete, outdated, or inconsistent and may reflect inherent limitations or biases. No guarantee is given regarding accuracy or suitability. Users may not use the Platform in a way that causes the Provider to violate third-party obligations.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. User Responsibilities</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Users must comply with applicable laws, use the Platform only for authorized educational purposes, avoid misuse or disruption, protect account credentials, and refrain from unauthorized commercial use or redistribution.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Accounts and Access</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Access requires a user account. Accounts may be suspended or terminated for violations or misuse, without notice and without refund in case of material breach.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. License and Permitted Use</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              A limited, non-exclusive, non-transferable, revocable license is granted for personal, non-commercial use only. Sharing credentials, scraping, downloading, redistribution, derivative works, or commercial use without permission is prohibited.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Digital Delivery & Access Duration</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Access is typically granted immediately after payment and may be time-limited or subject to expiration based on the purchased product or promotion.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Refunds & Withdrawal – Digital Content</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              By accessing digital content immediately, any statutory right of withdrawal is excluded once access has begun, as permitted by law. No refunds, cancellations, or chargebacks are available once the Platform has been accessed or used, regardless of satisfaction or outcomes. Mandatory rights in cases of complete technical non-delivery remain unaffected.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Promotions & Expiry</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Promotions may include specific access or expiration periods disclosed at purchase and may be modified with reasonable notice.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Inactive Accounts</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Accounts inactive for extended periods (e.g., 12 months) may be deactivated after prior notification.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Intellectual Property</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              All Content is owned by the Provider or licensors and protected by intellectual property laws. Unauthorized use is prohibited.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">13. Disclaimer Reference</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Use of the Platform is subject to the Exam & Legal Disclaimer, which forms an integral part of these Terms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">14. Limitation of Liability</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              To the maximum extent permitted by law, the Provider is not liable for exam outcomes, dissatisfaction, or indirect or consequential damages. Mandatory consumer rights remain unaffected.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">15. International Use</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              The Platform is operated from the United Kingdom. Users accessing from other jurisdictions do so at their own initiative and are responsible for local compliance.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">16. Language</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              The English version is legally binding. Translations are for convenience only.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">17. Governing Law</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              These Terms are governed by the laws of England and Wales, subject to mandatory consumer protections where applicable.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">18. Contact</h2>
            <p className="text-slate-600 dark:text-slate-300 mb-2">
              Margolytics Ltd., 128 City Road, London, EC1V 2NX, United Kingdom
            </p>
            <p className="text-slate-600 dark:text-slate-300">
              Email: <a href="mailto:info@margolytics.com" className="text-[#0047AB] hover:underline">info@margolytics.com</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfUse;
